import React from 'react';
import { RichTextParser, type EntityListRow as EntityListRowModel } from 'coop-adventure-engine';
import { RichTextRenderer } from './RichTextRenderer';

export interface EntityListProps {
  rows: EntityListRowModel[];
  onRowVisit?: (row: EntityListRowModel) => void;
  /**
   * Present when `<Entity-List>` embed uses reference selection; disables visit
   * and drives {@link onEntityReferencePick} instead.
   */
  referenceSelect?: {
    targetScene: string;
    variableName: string;
  };
  /** Fired with entity UUID when reference mode is active. */
  onEntityReferencePick?: (entityId: string) => void;
  /** When true, rows do not navigate (e.g. global action pending). */
  disableInteractions?: boolean;
  customColor?: string | undefined;
  className?: string;
  /** Optional empty-state message when there are no rows. */
  emptyMessage?: string;
}

/**
 * Renders engine-computed entity rows for an `<Entity-List>` embed.
 * When a row has `content` (from `contentVariable`), that rich text is the card body;
 * otherwise the plain `displayName` title is shown.
 */
const EntityList: React.FC<EntityListProps> = ({
  rows,
  onRowVisit,
  referenceSelect,
  onEntityReferencePick,
  disableInteractions = false,
  customColor,
  className = '',
  emptyMessage = 'No entities here yet.'
}) => {
  if (!rows.length) {
    return (
      <p
        className={`entity-list-empty my-3 text-sm opacity-80 ${className}`.trim()}
        data-testid="entity-list-empty"
        role="status"
      >
        {emptyMessage}
      </p>
    );
  }

  const refMode = Boolean(referenceSelect && onEntityReferencePick);

  return (
    <ul
      className={`entity-list activity-list my-4 space-y-3 list-none p-0 ${className}`.trim()}
      data-testid="entity-list"
      aria-label="Entities"
    >
      {rows.map((row) => {
        const canInteract =
          !disableInteractions &&
          (refMode ? Boolean(onEntityReferencePick) : Boolean(onRowVisit));
        const isActiveCard = canInteract;
        const activeStyle: React.CSSProperties | undefined =
          isActiveCard && customColor
            ? {
                border: `1px solid ${customColor}40`,
                background: `linear-gradient(135deg, ${customColor}88, ${customColor}AA)`
              }
            : undefined;

        const customContent =
          typeof row.content === 'string' ? row.content.trim() : '';
        const customRichText = customContent
          ? RichTextParser.parseRichTextDescription(customContent)
          : undefined;
        const useCustomContent = Boolean(
          customRichText && customRichText.elements.length > 0
        );
        const cardClass = `entity-card activity-card ${isActiveCard ? 'is-active' : 'is-inactive'}${
          useCustomContent ? ' entity-card--custom-content' : ''
        }`;

        const inner = useCustomContent ? (
          <div
            className="entity-card-custom-content activity-card-header text-left"
            data-testid={`entity-row-content-${row.entityId}`}
          >
            <RichTextRenderer
              description={customRichText!}
              className="rich-text entity-list-custom-content"
              {...(customColor ? { customColor } : {})}
            />
          </div>
        ) : (
          <div className="activity-card-header">
            <span className="activity-card-title">
              <span className="activity-card-title-leading">{row.displayName}</span>
            </span>
          </div>
        );

        if (canInteract) {
          return (
            <li key={row.entityId}>
              <button
                type="button"
                className={cardClass}
                style={activeStyle}
                onClick={() =>
                  refMode
                    ? onEntityReferencePick?.(row.entityId)
                    : onRowVisit?.(row)
                }
                data-testid={`entity-row-${row.entityId}`}
                aria-label={
                  refMode
                    ? `Select entity: ${row.displayName}`
                    : `Visit entity: ${row.displayName}`
                }
              >
                {inner}
              </button>
            </li>
          );
        }

        return (
          <li key={row.entityId}>
            <div
              className={cardClass}
              style={activeStyle}
              data-testid={`entity-row-${row.entityId}`}
              aria-disabled="true"
              role="group"
              aria-label={`${row.displayName}, unavailable`}
            >
              {inner}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default EntityList;
